import {
  VenueDetailsBySlugResponse,
  useBusinessInterests,
  useAuth,
  useI18N,
} from "common/hooks";
import { Helmet } from "react-helmet-async";
import { NearbyVenues } from "@/components/WhatsNearby/NearbyVenues";
import { useNearbyBusinesses } from "common/hooks/business/useNearbyBusinesses";
// Loader component removed as it's no longer used
import { Venue, BusinessInterestType } from "common/types";
import { useState, useMemo, useCallback } from "react";
import InterestNotificationModal from "@/components/Notifications/NotificationModal/InterestNotificationModal";
import { useAuthModals, AuthModalScreen } from "@/hooks/useAuthModals";

type WhatsNearbyContentProps = {
  onClose: () => void;
  venueDetailsResponse: VenueDetailsBySlugResponse | undefined;
  onRefresh: () => void;
  cid?: string;
  tid?: string;
  venueSlug: string;
  venueParam?: string;
};

function WhatsNearbyContent({
  venueDetailsResponse,
  onClose,
  venueParam,
}: WhatsNearbyContentProps) {
  const venueDetails = venueDetailsResponse.details;
  const { isLoggedIn } = useAuth();
  const { showModal } = useAuthModals();
  const i18n = useI18N();

  // Add nearby businesses query
  const { data: nearbyBusinessesData, isLoading: isLoadingNearbyBusinesses } =
    useNearbyBusinesses(venueDetails.business.id.toString());

  const hasAccessibilityData = venueDetails.tagIds !== "" ? true : false;

  // Get business interests to check which venues have notifications enabled
  const { data: businessInterests } = useBusinessInterests(false);

  // Notification modal state
  const [isNotificationModalOpen, setIsNotificationModalOpen] = useState(false);
  const [selectedVenueForNotification, setSelectedVenueForNotification] =
    useState<{
      id: string;
      name: string;
      interestId?: string;
      interestedType?: string;
      notifyAccessibilityInformation: boolean;
      notifyLiveAlert: boolean;
    } | null>(null);

  // Create a Set of venue IDs with notifications enabled
  const notificationVenueIds = useMemo(() => {
    if (!businessInterests) return new Set<number>();

    return new Set(
      businessInterests
        .filter(
          (interest) =>
            interest.notify_accessibility_information === 1 ||
            interest.notify_live_alert === 1,
        )
        .map((interest) => interest.business_id),
    );
  }, [businessInterests]);

  // Create a Map for quick lookup of business interest details
  const businessInterestMap = useMemo(() => {
    if (!businessInterests) return new Map<number, BusinessInterestType>();

    return new Map<number, BusinessInterestType>(
      businessInterests.map(
        (interest) =>
          [interest.business_id, interest] as [number, BusinessInterestType],
      ),
    );
  }, [businessInterests]);

  // Handle get alert
  const handleGetAlert = useCallback(
    (venueId: string | number) => {
      if (!isLoggedIn) {
        showModal(AuthModalScreen.EnterEmail);
        return;
      }

      // Get nearby venues from the API response
      const nearbyVenues = nearbyBusinessesData?.data?.nearbyBusinessList || [];
      const venue = nearbyVenues.find((v: any) => v.id === venueId);

      if (venue) {
        const businessId = venue.business?.id;

        // Check if this venue already has an interest record
        const existingInterest: BusinessInterestType | undefined = businessId
          ? businessInterestMap.get(businessId)
          : undefined;

        setSelectedVenueForNotification({
          id: businessId?.toString() || venueId.toString(),
          name: venue.name,
          interestId: existingInterest?.id.toString(),
          interestedType: existingInterest?.interested_type,
          notifyAccessibilityInformation:
            existingInterest?.notify_accessibility_information === 1,
          notifyLiveAlert: existingInterest?.notify_live_alert === 1,
        });
        setIsNotificationModalOpen(true);
      }
    },
    [isLoggedIn, showModal, nearbyBusinessesData, businessInterestMap],
  );

  const handleNotificationModalClose = useCallback(() => {
    setIsNotificationModalOpen(false);
  }, []);

  return (
    <>
      <Helmet>
        <title>
          {venueDetails?.name
            ? `${venueDetails.name.length > 30 ? venueDetails.name.substring(0, 30) + "..." : venueDetails.name}`
            : ""}
          | {i18n.get("whatsNearby")} | Sociability
        </title>
      </Helmet>

      {/* Existing Nearby Venues */}
      <div className="w-full h-full flex flex-col">
        {isLoadingNearbyBusinesses ? (
          <div
            className="min-h-screen w-full"
            role="status"
            aria-label={i18n.get("loadingNearbyVenues") || "Loading nearby venues"}
          >
            {/* Progressive loading - show skeleton UI */}
            <div className="animate-pulse" aria-hidden="true">
              {/* Hero Section Skeleton */}
              <div className="px-6 lg:px-12 pt-12 pb-6 bg-container-brand-minimal">
                <div className="max-w-[1440px] mx-auto">
                  <div className="h-8 bg-container-brand-minimal rounded w-1/3 mb-4"></div>
                  <div className="h-4 bg-container-brand-minimal rounded w-1/2"></div>
                </div>
              </div>

              {/* Content Skeleton */}
              <div className="max-w-[1440px] mx-auto p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  {/* Left side skeleton */}
                  <div className="space-y-4">
                    <div className="h-6 bg-container-brand-minimal rounded w-1/4"></div>
                    {[...Array(3)].map((_, i) => (
                      <div key={i} className="border rounded-lg p-4">
                        <div className="flex gap-4">
                          <div className="w-24 h-24 bg-container-brand-minimal rounded"></div>
                          <div className="flex-1 space-y-2">
                            <div className="h-4 bg-container-brand-minimal rounded w-3/4"></div>
                            <div className="h-3 bg-container-brand-minimal rounded w-1/2"></div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Right side skeleton */}
                  <div className="h-96 bg-container-brand-minimal rounded-lg"></div>
                </div>
              </div>
            </div>
          </div>
        ) : !nearbyBusinessesData ? (
          <div className="flex flex-col items-center justify-center py-8">
            <p className="text-center label-m-medium text-fg-brand-subtle">
              {i18n.get("noNearbyVenuesDataAvailable")}
            </p>
          </div>
        ) : (
          <NearbyVenues
            categories={
              nearbyBusinessesData?.data?.nearbyBusinessCategories || []
            }
            nearbyBusinessList={
              nearbyBusinessesData?.data?.nearbyBusinessList || []
            }
            venueLatitude={venueDetails.latitude}
            venueLongitude={venueDetails.longitude}
            venueId={venueDetails.business.id.toString()}
            nearbyFilterAccessibility={
              nearbyBusinessesData?.data?.filter_accessibility || []
            }
            accessibilityFeaturesRequirementsHiddenIds={
              nearbyBusinessesData?.data?.accessibility_features_requirements_hidden_ids?.map(
                String,
              ) || []
            }
            accessibilityFeaturesRequirementsShowHiddenById={
              nearbyBusinessesData?.data?.accessibility_features_requirements_show_hidden_by_id?.toString() ||
              ""
            }
            hasAccessibilityData={hasAccessibilityData || false}
            venueDetails={venueDetails as unknown as Venue}
            businessPhotoFirst={venueDetails.businessPhotoFirst?.path || ""}
            onClose={onClose}
            onGetAlert={handleGetAlert}
            notificationVenueIds={notificationVenueIds}
            isLoggedIn={isLoggedIn}
            onShowAuthModal={() => showModal(AuthModalScreen.EnterEmail)}
            venueParam={venueParam}
          />
        )}
      </div>

      {/* Notification Modal */}
      {selectedVenueForNotification && (
        <InterestNotificationModal
          businessId={selectedVenueForNotification.id}
          businessName={selectedVenueForNotification.name}
          interestId={selectedVenueForNotification.interestId}
          interestedType={selectedVenueForNotification.interestedType}
          notifyAccessibilityInformation={
            selectedVenueForNotification.notifyAccessibilityInformation
          }
          notifyLiveAlert={selectedVenueForNotification.notifyLiveAlert}
          isOpen={isNotificationModalOpen}
          onClose={handleNotificationModalClose}
          onSuccess={() => {
            console.log("Notification preferences saved successfully");
          }}
        />
      )}
    </>
  );
}

export default WhatsNearbyContent;
